import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonService } from '@src/app/services/common.service';
import { WalletStatusService } from '@src/app/services/wallet-status.service';
import { ChainEnum } from '@src/app/shared/types/wallet-chain.type';
import { formatNumberWithPrefix, formatTinyNumber, isTinyNumber } from '@src/app/shared/utils/number.util';
import { uniqBy } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, takeUntil } from 'rxjs/operators';
import { MemeDashboardStatusService } from '../../meme-dashboard-status.service';
import { MemeDashboardService } from '../../meme-dashboard.service';
import { TrackOption, WatchList, WatchlistTGAlert } from '../../meme-dashboard.type';
@Component({
    selector: 'app-my-watchlist',
    templateUrl: './my-watchlist.component.html',
    styleUrls: ['./my-watchlist.component.less'],
})
export class MyWatchlistComponent implements OnInit, OnDestroy {
    formatNumberWithPrefix = formatNumberWithPrefix;
    isTinyNumber = isTinyNumber;
    formatTinyNumber = formatTinyNumber;

    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    searchedAddress: string;

    filteredWatchList: WatchList['address_list'] = [];
    watchList: WatchList['address_list'] = [];
    total = 0;
    isLastPage = false;

    recentTgAlerts: WatchlistTGAlert[];

    options: { label: string; value: 'all' | TrackOption }[] = [
        { label: 'All', value: 'all' },
        { label: 'My Watchlist', value: 'my_watchlist' },
        { label: 'SocialScan Smart Money', value: 'smart_buys' },
        { label: 'Subscribed Watchlist', value: 'subscribed_watchlist' },
    ];

    columns = [
        { name: '', width: '50px' },
        { name: 'No.', width: '100px' },
        { name: 'Wallets', width: '240px' },
        { name: 'MEME Networth' },
        { name: '7D Realized PnL' },
        { name: '30D Realized PnL' },
        { name: 'Win Rate' },
    ];

    editingNoteAddresses: { address: string; label: string }[] = [];

    unsubscribedAddresses: string[] = [];

    savingNotesAddresses: string[] = [];

    pageInfo = { page: 1, size: 30 };

    selectedAlertType: 'all' | TrackOption = 'all';

    tabIndex = 0;

    fetchingRecentTgAlerts = false;

    fetchingWatchlistWallets = false;

    chain: any = ChainEnum.BASE;

    get isWatchListAdded() {
        return this.walletStatusService.userProfile?.watchlist?.address_added;
    }

    get agentInfo() {
        return this.memeDashboardStatusService.myWatchlistAgentInfo$.getValue();
    }

    constructor(
        public commonService: CommonService,
        public memeDashboardService: MemeDashboardService,
        public memeDashboardStatusService: MemeDashboardStatusService,
        private walletStatusService: WalletStatusService
    ) {}

    ngOnInit() {
        this.fetchAlerts();

        this.fetchWatchlistWallets();

        this.fetchMyWatchlistAgentInfo();

        // 添加搜索订阅
        this.searchSubject
            .pipe(
                debounceTime(300), // 300ms 防抖
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(searchText => {
                if (!searchText) {
                    this.filteredWatchList = this.watchList;
                    return;
                }

                this.filteredWatchList = this.watchList.filter(
                    item =>
                        item.address.toLowerCase().includes(searchText.toLowerCase()) ||
                        (item.label && item.label.toLowerCase().includes(searchText.toLowerCase()))
                );
            });

        // 添加新地址成功订阅
        this.memeDashboardStatusService.newAddedWatchlistCount$
            .pipe(
                filter(count => count > 0),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.memeDashboardStatusService.fetchMyWatchlistWallets(this.chain);
                this.fetchWatchlistWallets();
                this.fetchMyWatchlistAgentInfo();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    fetchMyWatchlistAgentInfo() {
        this.memeDashboardService.getMyWatchListAgentInfo().subscribe({
            next: res => this.memeDashboardStatusService.myWatchlistAgentInfo$.next(res),
            error: err => this.commonService.errorMessageByResponse(err),
        });
    }

    fetchWatchlistWallets() {
        this.fetchingWatchlistWallets = true;
        this.memeDashboardService
            .getWatchlist(this.chain)
            .then(data => {
                this.watchList = data.address_list;
                this.filteredWatchList = data.address_list;
            })
            .finally(() => (this.fetchingWatchlistWallets = false));
    }

    fetchAlerts() {
        if (this.fetchingRecentTgAlerts) return;
        this.fetchingRecentTgAlerts = true;
        this.memeDashboardService
            .getUserWatchlistTGAlerts(this.selectedAlertType, this.pageInfo.page, this.pageInfo.size, this.chain)
            .pipe(finalize(() => (this.fetchingRecentTgAlerts = false)))
            .subscribe({
                next: ({ data, total, page }) => {
                    this.total = total;
                    this.recentTgAlerts = (this.recentTgAlerts || []).concat(data);
                    this.isLastPage = this.recentTgAlerts.length >= total;
                    this.pageInfo.page = page + 1;
                },
                error: err => {
                    this.commonService.errorMessageByResponse(err);
                    this.isLastPage = true;
                },
            });
    }

    // 修改搜索方法
    onSearch(value: string) {
        this.searchSubject.next(value);
    }

    startEdit(item: any) {
        this.editingNoteAddresses = uniqBy([...this.editingNoteAddresses, { address: item.address, label: item.label }], 'address');
    }

    stopEdit(address: string) {
        this.editingNoteAddresses = this.editingNoteAddresses.filter(item => address !== item.address);
    }

    getEditingNote(address: string): string {
        return this.editingNoteAddresses.find(item => item.address === address)?.label || '';
    }

    setEditingNote(address: string, label: string) {
        const index = this.editingNoteAddresses.findIndex(item => item.address === address);
        if (index > -1) {
            this.editingNoteAddresses[index].label = label;
        }
    }

    isEditing(address: string) {
        return this.editingNoteAddresses.some(item => item.address === address);
    }

    saveNotes(address: string) {
        if (this.savingNotesAddresses.includes(address)) return;

        this.savingNotesAddresses.push(address);
        const label = this.editingNoteAddresses.find(item => item.address === address)?.label;
        this.memeDashboardService
            .addLabel(address, label)
            .then(() => {
                this.editingNoteAddresses = this.editingNoteAddresses.filter(item => item.address !== address);
                this.watchList.find(item => item.address === address).label = label;
            })
            .finally(() => {
                this.savingNotesAddresses = this.savingNotesAddresses.filter(item => item !== address);
            });
    }

    addAddressToWatchlist(address: string) {
        this.unsubscribedAddresses = this.unsubscribedAddresses.filter(item => item !== address);
        this.memeDashboardService.addAddressToWatchlist(address).then(isSuccess => {
            if (!isSuccess) {
                this.unsubscribedAddresses.push(address);
            }
        });
    }

    removeAddressFromWatchlist(address: string) {
        this.unsubscribedAddresses.push(address);
        this.memeDashboardService.changeAddressesFromWatchlist({ address_to_delete: [address] }).catch(err => {
            this.unsubscribedAddresses = this.unsubscribedAddresses.filter(item => item !== address);
            this.commonService.errorMessageByResponse(err);
        });
    }

    toggleAlertType(type: TrackOption | 'all') {
        this.selectedAlertType = type;
        this.recentTgAlerts = [];
        this.pageInfo.page = 1;
        this.isLastPage = false;
        this.fetchAlerts();
    }

    chainChange() {
        this.recentTgAlerts = [];
        this.pageInfo.page = 1;
        this.isLastPage = false;
        this.fetchAlerts();
        this.fetchWatchlistWallets();
    }
}
